/**
 * Notification Sound Utility
 * Plays a pleasant notification sound when new notifications arrive
 */

class NotificationSound {
  private audioContext: AudioContext | null = null;
  private enabled: boolean = true;

  constructor() {
    // Initialize AudioContext on first user interaction to comply with browser policies
    if (typeof window !== 'undefined') {
      this.initAudioContext();
    }
  }

  private initAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
    }
  }

  /**
   * Play a pleasant notification sound
   */
  play() {
    if (!this.enabled || !this.audioContext) {
      return;
    }

    try {
      const now = this.audioContext.currentTime;
      
      // Create a smooth, pleasant notification sound
      // Using a gentle ascending tone pattern
      
      // First note (E5 - 659.25 Hz)
      const osc1 = this.audioContext.createOscillator();
      const gain1 = this.audioContext.createGain();
      
      osc1.type = 'sine'; // Sine wave for smooth tone
      osc1.frequency.setValueAtTime(659.25, now);
      
      osc1.connect(gain1);
      gain1.connect(this.audioContext.destination);
      
      // Smooth envelope
      gain1.gain.setValueAtTime(0, now);
      gain1.gain.linearRampToValueAtTime(0.15, now + 0.02);
      gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
      
      osc1.start(now);
      osc1.stop(now + 0.4);
      
      // Second note (A5 - 880 Hz) - perfect fourth interval
      const osc2 = this.audioContext.createOscillator();
      const gain2 = this.audioContext.createGain();
      
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(880, now);
      
      osc2.connect(gain2);
      gain2.connect(this.audioContext.destination);
      
      gain2.gain.setValueAtTime(0, now + 0.08);
      gain2.gain.linearRampToValueAtTime(0.12, now + 0.1);
      gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
      
      osc2.start(now + 0.08);
      osc2.stop(now + 0.5);
      
      // Add subtle harmonic for richness (very quiet)
      const osc3 = this.audioContext.createOscillator();
      const gain3 = this.audioContext.createGain();
      
      osc3.type = 'sine';
      osc3.frequency.setValueAtTime(1318.5, now); // E6 - octave + fifth
      
      osc3.connect(gain3);
      gain3.connect(this.audioContext.destination);
      
      gain3.gain.setValueAtTime(0, now);
      gain3.gain.linearRampToValueAtTime(0.05, now + 0.02);
      gain3.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
      
      osc3.start(now);
      osc3.stop(now + 0.35);
      
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  }

  /**
   * Enable notification sounds
   */
  enable() {
    this.enabled = true;
    if (!this.audioContext) {
      this.initAudioContext();
    }
  }

  /**
   * Disable notification sounds
   */
  disable() {
    this.enabled = false;
  }

  /**
   * Toggle notification sounds
   */
  toggle() {
    this.enabled = !this.enabled;
    return this.enabled;
  }

  /**
   * Check if sounds are enabled
   */
  isEnabled() {
    return this.enabled;
  }
}

// Export singleton instance
export const notificationSound = new NotificationSound();
